import {ref} from 'vue'
import {useAuthStore} from "@/stores/authStore.js";
import router from '@/router';
import {CORRELATION_HEADER, newCorrelationId} from '@/utils/correlation.js';
import {qrRejectionMessage} from '@/utils/qrMessages.js';

const isAuthenticated = ref(false)

// The request could not be made as the signed-in rep (expired session, failed
// refresh). Not an answer from the server: services/scanQueue.js keeps the scan.
const authRequired = (message) => Object.assign(new Error(message), {code: 'auth_required'})

/**
 * Set when the api refuses this build's brand for the signed-in rep - a valid
 * token, wrong tenant. Null the rest of the time. App.vue watches it and offers
 * to sign out; nothing clears the session on its own, because the usual cause is
 * a rep opening the wrong brand's URL rather than anything being wrong with
 * their account.
 */
const brandDenied = ref(null)
class Api {
    constructor() {
        this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.snowfuze.com'
        this.refreshToken = localStorage.getItem('refreshToken')
        this.isRefreshing = false
        this.refreshSubscribers = []
        this.lastRefreshFailureType = null // 'network' | 'invalid' | 'other'
    }

    async request(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;

        // Minted here, not per fetch: a 401 refresh or a network retry is the
        // same logical call, and an offline request replayed hours later keeps
        // the id it was captured under.
        const correlationId = options.correlationId || newCorrelationId();

        const headers = {
            'Content-Type': 'application/json',
            'X-Fuze-Brand': __DICT__.brand,
            ...options.headers,
            [CORRELATION_HEADER]: correlationId,
        };

        if (options.responseType === 'arraybuffer') {
            delete headers['Content-Type'];
        }

        const config = {
            ...options,
            headers,
            credentials: 'include',
        };

        // If there's already a refresh in progress and this isn't a refresh request,
        // wait for the refresh to complete before proceeding
        if (this.isRefreshing && !options.isRefreshRequest) {
            const refreshResult = await new Promise(resolve => {
                this.refreshSubscribers.push(resolve);
            });

            if (!refreshResult) {
                throw authRequired('Authentication failed during token refresh');
            }
        }

        try {
            let response = await fetch(url, config);
            // Handle 401 (Unauthorized) response
            if (response.status === 401 && !options.isRefreshRequest) {
                if (this.refreshToken) {
                    const refreshSuccess = await this.refreshTokens();
                    if (refreshSuccess) {
                        // Retry the original request
                        return this.request(endpoint, {...options, correlationId});
                    } else {
                        // Only log out (locally) if the refresh token is invalid; otherwise, treat as temporary failure
                        if (this.lastRefreshFailureType === 'invalid') {
                            await this.doLogout({ server: false });
                            throw authRequired('Authentication failed - please log in again');
                        } else {
                            throw authRequired('Temporary authentication issue. Please try again.');
                        }
                    }
                } else {
                    // No refresh token available: perform local sign-out without hitting server logout
                    await this.doLogout({ server: false });
                    throw authRequired('Session expired - please log in again');
                }
            }

            // The token is valid, the brand it was issued for is not this one.
            // Deliberately not treated as a 401: refreshing would mint another
            // token with the same entitlement, so a retry is pointless, and
            // signing the rep out silently would cost them a login at a door for
            // what is usually a mis-tapped brand URL. App.vue offers logout.
            if (response.status === 403 && !options.isRefreshRequest) {
                let denied = null;
                try {
                    denied = await response.clone().json();
                } catch {
                    // A 403 from elsewhere with no JSON body; fall through to the
                    // generic handler below.
                }
                if (denied?.code === 'brand_not_permitted') {
                    brandDenied.value = { brand: denied.brand ?? null, message: denied.error };
                    const error = new Error(denied.error || 'You do not have access to this brand');
                    error.status = 403;
                    error.code = 'brand_not_permitted';
                    error.correlationId = correlationId;
                    throw error;
                }
            }

            if (!response.ok) {
                let errorMessage;
                let errorType = null;
                let errorData = null;
                try {
                    errorData = await response.json();
                    errorMessage = errorData?.message || errorData?.error || `Request failed with status ${response.status}`;
                    // Server-side QR signature rejection, { error, qrVerification }:
                    // every view shows error.message, so make it the clear one.
                    if (errorData?.qrVerification) errorMessage = qrRejectionMessage(errorData);
                    errorType = errorData?.error;
                } catch (e) {
                    errorMessage = `Request failed with status ${response.status}`;
                }
                // Create error with additional context for timeout handling
                const error = new Error(errorMessage);
                error.status = response.status;
                // Read by Sentry's beforeSend in main.js, which lifts it onto
                // the event as a searchable tag.
                error.correlationId = correlationId;
                error.errorType = errorType;
                if (errorData?.code) error.code = errorData.code;
                if (errorData?.setupUrl) error.setupUrl = errorData.setupUrl;
                if (errorData?.retryAfter) error.retryAfter = errorData.retryAfter;
                if (errorData?.lastUsed) error.lastUsed = errorData.lastUsed;
                if (errorData?.qrVerification) error.qrVerification = errorData.qrVerification;
                // A duplicate scan: the first attempt's outcome (services/scanQueue.js).
                if (errorData && 'original' in errorData) error.original = errorData.original;
                throw error;
            }

            isAuthenticated.value = true;

            // Handle no-content responses safely (Safari may throw TypeError: Load failed on empty bodies)
            if (response.status === 204 || response.status === 205) {
                return null;
            }

            if (options.responseType === 'arraybuffer') {
                return await response.arrayBuffer();
            } else if (options.responseType === 'text') {
                return await response.text();
            } else {
                const contentType = response.headers.get('content-type') || '';
                if (contentType.includes('application/json')) {
                    return await response.json();
                }
                // Fallback to text if not JSON
                return await response.text();
            }
        } catch (error) {
            // Network failures never reached the block above, so they carried
            // no id at all - the one case where a Sentry event has no matching
            // api log and the id is the only evidence the call was attempted.
            if (error && !error.correlationId) error.correlationId = correlationId;
            console.error(`API request failed [${correlationId}]:`, error);

            // Detect timeout errors from service worker
            const isTimeout = error.errorType === 'timeout' || error.status === 504 ||
                (error.message && error.message.toLowerCase().includes('timeout'));

            // Detect Safari and general network-like errors
            const isNetworkish = (() => {
                try {
                    const msg = (error && error.message ? String(error.message) : '').toLowerCase();
                    const offline = (typeof navigator !== 'undefined' && navigator.onLine === false);
                    return (
                        error instanceof TypeError ||
                        msg.includes('failed to fetch') ||
                        msg.includes('load failed') ||
                        msg.includes('network request failed') ||
                        msg.includes('networkerror') ||
                        msg.includes('network error') ||
                        msg.includes('the network connection was lost') ||
                        msg.includes('cancelled') ||
                        offline ||
                        isTimeout
                    );
                } catch (_) {
                    return false;
                }
            })();

            // If it's a network error and not already a refresh request, try refreshing token once
            if (isNetworkish && this.refreshToken && !options.isRefreshRequest) {
                try {
                    const refreshSuccess = await this.refreshTokens();
                    if (refreshSuccess) {
                        // Retry the original request one more time
                        return this.request(endpoint, {...options, correlationId});
                    } else {
                        if (this.lastRefreshFailureType === 'invalid') {
                            await this.doLogout({ server: false });
                            throw authRequired('Session expired - please log in again');
                        } else {
                            // Network or temporary issue; rethrow original network error
                            throw error;
                        }
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    if (this.lastRefreshFailureType === 'invalid') {
                        await this.doLogout();
                        throw authRequired('Session expired - please log in again');
                    } else {
                        // Rethrow original network error to allow retry without logging out
                        throw error;
                    }
                }
            }

            throw error;
        }
    }

    async refreshTokens() {
        if (this.isRefreshing) {
            return new Promise(resolve => {
                this.refreshSubscribers.push(resolve)
            })
        }

        if (!this.refreshToken) {
            this.lastRefreshFailureType = 'invalid'
            return false
        }

        this.isRefreshing = true
        this.lastRefreshFailureType = null

        try {

            const response = await this.request('/auth/refresh', {
                method: 'POST',
                body: JSON.stringify({refreshToken: this.refreshToken}),
                isRefreshRequest: true
            })


            this.refreshToken = response.refreshToken
            localStorage.setItem('refreshToken', this.refreshToken)

            this.isRefreshing = false
            this.refreshSubscribers.forEach(callback => callback(true))
            this.refreshSubscribers = []

            return true
        } catch (error) {
            console.error('Token refresh failed:', error)
            // Classify failure type to avoid logging out users on transient issues
            if (error instanceof TypeError || (error.message && (
                    error.message.toLowerCase().includes('failed to fetch') ||
                    error.message.toLowerCase().includes('load failed') ||
                    error.message.toLowerCase().includes('network request failed') ||
                    error.message.toLowerCase().includes('networkerror') ||
                    error.message.toLowerCase().includes('network error') ||
                    error.message.toLowerCase().includes('the network connection was lost') ||
                    error.message.toLowerCase().includes('cancelled')
                )) || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
                this.lastRefreshFailureType = 'network'
            } else if (error.message && (error.message.toLowerCase().includes('invalid') || error.message.toLowerCase().includes('expired') || error.message.toLowerCase().includes('unauthorized'))) {
                this.lastRefreshFailureType = 'invalid'
            } else {
                this.lastRefreshFailureType = 'other'
            }

            this.isRefreshing = false
            this.refreshSubscribers.forEach(callback => callback(false))
            this.refreshSubscribers = []
            
            // Only remove refresh token if it's actually invalid (not network errors)
            if (this.lastRefreshFailureType === 'invalid') {
                localStorage.removeItem('refreshToken')
                this.refreshToken = null
            }
            
            return false
        }
    }

    async login(username, password) {
        const response = await this.request('/auth/login', {
            method: 'POST',
            body: JSON.stringify({username, password})
        })

        // Two factor accounts get a challenge instead of a session; nothing to store yet
        if (response.twoFactorRequired) {
            return response
        }

        this.refreshToken = response.refreshToken
        localStorage.setItem('refreshToken', this.refreshToken)
        return response
    }

    // The two factor challenge is identified by an opaque challengeId from /auth/login.
    // isRefreshRequest stops a 401 (wrong code, expired challenge) being mistaken for an
    // expired session and triggering a token refresh.
    async sendTwoFactorCode(challengeId, channel) {
        return this.request('/auth/two-factor/send', {
            method: 'POST',
            body: JSON.stringify({challengeId, channel}),
            isRefreshRequest: true
        })
    }

    async verifyTwoFactor({challengeId, code, recoveryCode}) {
        const response = await this.request('/auth/two-factor', {
            method: 'POST',
            body: JSON.stringify({challengeId, code, recoveryCode}),
            isRefreshRequest: true
        })

        if (response.refreshToken) {
            this.refreshToken = response.refreshToken
            localStorage.setItem('refreshToken', this.refreshToken)
        }

        return response
    }

    async logout(options = { server: true }) {
        const { server = true } = options || {}
        localStorage.getItem('weekId')
        // Before /auth/logout ends the session: remove this push device so the
        // phone stops receiving this staff member's notifications (notifications-v2).
        // isRefreshRequest keeps a dead session from refreshing or logging out again.
        // Imported lazily: pushDevice -> api would be a cycle.
        try {
            const { removePushDevice } = await import('@/services/pushDevice.js')
            await removePushDevice({
                server: server && !!this.refreshToken,
                apiClient: { post: (endpoint, data) => this.post(endpoint, data, { isRefreshRequest: true }) }
            })
        } catch (e) {
            console.warn('Push device removal failed:', e?.message || e)
        }
        if (server && this.refreshToken) {
            try {
                await this.request('/auth/logout', {
                    method: 'POST',
                    body: JSON.stringify({ refreshToken: this.refreshToken }),
                    isRefreshRequest: true
                })
            } catch (e) {
                console.warn('Server logout failed or skipped:', e?.message || e)
                // Continue with local cleanup regardless
            }
        }
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('pwa_pin_hash')
        localStorage.removeItem('pin_salt')
        localStorage.removeItem('last_activity')
        localStorage.removeItem('pwaInstallDeclined')
        localStorage.removeItem('weekId')
        this.refreshToken = null
        isAuthenticated.value = false
    }

    get(endpoint, options = {}) {
        return this.request(endpoint, {...options, method: 'GET'})
    }

    /**
     * QR signing keys for a week, decrypted. Key material: never log the
     * response. `since` (unix seconds, the previous syncedAt) asks for changes only.
     */
    getQrKeys(weekId, since = null) {
        const params = new URLSearchParams({weekId: String(weekId)})
        if (since !== null && since !== undefined) params.set('since', String(since))
        return this.get(`/api/qr-keys?${params}`, {headers: {'Cache-Control': 'no-store'}})
    }

    /** Public flag lookup resolved for this build's tenant. */
    getFlag(slug) {
        return this.get(`/flags/${encodeURIComponent(slug)}?tenant=${encodeURIComponent(__DICT__.brand)}`)
    }


    post(endpoint, data, options = {}) {
        return this.request(endpoint, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data),
        })
    }

    // Check current session status using the new /auth/status endpoint.
    // Does not force logout on temporary errors; suitable for intermittent connectivity.
    async authStatus() {
        try {
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                return { authenticated: !!this.refreshToken, offline: true }
            }
            const res = await this.get('/auth/status', { headers: { 'Cache-Control': 'no-cache' } })
            isAuthenticated.value = !!res.authenticated
            return res
        } catch (error) {
            // If status check fails (including 401), reflect unauthenticated without logging out here.
            isAuthenticated.value = false
            return { authenticated: false, error: error.message || 'Auth status check failed' }
        }
    }

    async doLogout (options = { server: true }) {
        const authStore = useAuthStore()
        await api.logout(options)
        authStore.setPwaUnlocked(false)
        authStore.setLoggedIn(false)
        authStore.logOut()
        await router.push({name: 'Login'})
    }


}

export const api = new Api()
export {isAuthenticated, brandDenied}
